import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["hideSidePanelNextButton", "enabled", "stepKey", "label"];
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { Footer, BackButton } from 'signup-components/components/SidePanelLayout/SidePanelLayout';
import { Step } from 'signup-constants/Step';
import { getNextSteps } from '../../store/flow/selectors';
import { goPrevStep } from '../../store/flow/action-creators';
import { useTracker } from '../../tracking/useTracker';
import { getIsAccountCreationStep } from '../../flow-management/getIsAccountCreationStep';
import { useCanGoBack } from '../../hooks/useCanGoBack';
import { useCurrentStep } from '../../hooks/useCurrentStep';
import { useIsStepDataValid } from '../../hooks/useIsStepDataValid';
import { useView } from '../useView';
import { usePrivacyBanner } from '../../views-components/usePrivacyBanner';
import HubletLocation from '../../views-components/HubletLocation';
import CreateAccountTermsOfService from '../../views-components/CreateAccountTermsOfService';
import { useIndustry } from '../../step-industry/useIndustry';
import { useResendEmail } from '../../hooks/useResendEmail';
import { useSearchAndPrefillExperiment } from '../../hooks/useSearchAndPrefillExperiment';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const SidePanelBaseViewFooter = _ref => {
  let {
      hideSidePanelNextButton,
      enabled,
      stepKey,
      label
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const Tracker = useTracker();
  const dispatch = useDispatch();
  const {
    currentStep
  } = useCurrentStep();
  const dataSeleniumAttr = `${currentStep}_button`;
  const isStepDataValid = useIsStepDataValid(currentStep);
  const isEnabled = typeof enabled === 'boolean' ? enabled : isStepDataValid;
  const {
    clickedToSeeAll,
    submitIndustry,
    predictionsFetchedGTKM
  } = useIndustry();
  const isIndustrySearch = stepKey === Step.Industry && clickedToSeeAll;
  const {
    resend
  } = useResendEmail();
  const {
    submit
  } = useView({
    stepKey
  });
  const {
    trackCompanyNameChanged
  } = useSearchAndPrefillExperiment();
  const onSubmit = event => {
    event.preventDefault();
    if (currentStep === Step.ResendEmail) {
      resend();
    }
    if (currentStep === Step.CompanyName) {
      trackCompanyNameChanged();
    }
    if (isIndustrySearch) {
      submitIndustry();
    } else {
      submit();
    }
  };
  const {
    shouldShowToSNonOptionalStep,
    shouldShowToSOptionalStep
  } = usePrivacyBanner();
  const isAccountCreationStep = useSelector(state => getIsAccountCreationStep(state, getNextSteps(state)));
  const showCreateAccountText = shouldShowToSNonOptionalStep || shouldShowToSOptionalStep || isAccountCreationStep;
  const buttonLabel = useMemo(() => {
    if (currentStep === Step.ResendEmail) {
      return 'button.resendEmail';
    }
    if (showCreateAccountText) {
      return 'button.createAccount';
    }
    if (label && isValidI18nKey(label)) {
      return label;
    }
    const customButtonLabelI18n = `views.${currentStep}.buttonLabel`;
    if (isValidI18nKey(customButtonLabelI18n)) {
      return customButtonLabelI18n;
    }
    return 'button.next';
  }, [showCreateAccountText, label, currentStep]);
  const shouldShowToSOptionalPhonenumber = shouldShowToSOptionalStep && currentStep === Step.OptionalPhoneNumber;
  const shouldShowTermsOfService = shouldShowToSNonOptionalStep || shouldShowToSOptionalPhonenumber;
  const {
    canGoBack,
    completedStep
  } = useCanGoBack();
  const shouldShowBackButton = Boolean(canGoBack && completedStep);
  const onBackLinkClick = event => {
    event.preventDefault();
    Tracker.backButton(currentStep, completedStep);
    dispatch(goPrevStep());
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(Footer, {
      children: [shouldShowBackButton && /*#__PURE__*/_jsx(BackButton, {
        onClick: onBackLinkClick,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'button.back'
        })
      }), !shouldShowTermsOfService && showCreateAccountText && /*#__PURE__*/_jsx(View, {
        alignSelf: "center",
        children: /*#__PURE__*/_jsx(Text, {
          color: "text-primary-disabled",
          marginInlineEnd: "400",
          textAlign: "right",
          typography: "detail-200",
          children: /*#__PURE__*/_jsx(HubletLocation, {})
        })
      }), shouldShowTermsOfService && /*#__PURE__*/_jsx(View, {
        alignSelf: "center",
        children: /*#__PURE__*/_jsx(CreateAccountTermsOfService, {
          hideBorder: shouldShowToSOptionalPhonenumber
        })
      }), /*#__PURE__*/_jsx(View, {
        children: (!hideSidePanelNextButton || isIndustrySearch || predictionsFetchedGTKM || stepKey === Step.HubletSelection) && /*#__PURE__*/_jsx(LoadingButton, Object.assign({
          "data-selenium": dataSeleniumAttr,
          disabled: !isEnabled,
          size: "medium",
          type: "submit",
          use: "primary",
          onClick: onSubmit
        }, props, {
          children: /*#__PURE__*/_jsx(Flex, {
            alignItems: "center",
            justifyContent: "center",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: buttonLabel
            })
          })
        }))
      })]
    })
  });
};